import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import sfAuth from "~/auth/SFAuth";
import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import canOpenTenantSettings from "~/helpers/canOpenTenantSettings";
import { buildNavigationCommands, type CommandDefinition } from "~/helpers/search/commandCatalog";
import { getPageSearchIndex } from "~/helpers/search/pageSearchIndex";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import useCanManageTenantSettings from "~/hooks/useCanManageTenantSettings";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useIsSiteRequiredRoute from "~/hooks/useIsSiteRequiredRoute";
import globalSearchFocus from "~/keyboard/globalSearchFocus";
import focusRail from "~/keyboard/railFocus";
import { focusActiveRibbonBand } from "~/keyboard/ribbonRoving";
import shortcutsOverlayControl from "~/keyboard/shortcutsOverlayControl";
import runGuardedExit from "~/layout/unsavedChangesRegistry";
import AppThemeContext from "~/providers/AppThemeContext";
import { openPreferencesModal, toggleAiPanel, togglePanel, toggleProfilePanel } from "~/store/layoutSlice";
import { updateSite } from "~/store/siteSlice";

const DISPLAY_SETTINGS_PATH = "/settings/preferences#display-mode";
const PREFERENCES_PATH = "/settings/preferences";
const PROFILE_PATH = "/settings/profile";
const SETTINGS_PATH = "/settings";
const INTEGRATIONS_PATH = "/settings/integrations";

const useCommandCatalog = (): CommandDefinition[] => {
	const { t } = useTranslation("common");
	const canManageSettings = useCanManageTenantSettings();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { setThemeType, themeType } = useContext(AppThemeContext);
	const { isEnabled: isAiChatEnabled } = useFeatureFlag(FeatureFlagName.AiChat);
	const siteRequired = useIsSiteRequiredRoute();
	const hasSite = useAppSelector((state) => state.site.location !== undefined);

	return useMemo<CommandDefinition[]>(() => {
		const actionCommands: CommandDefinition[] = [
			{
				category: t("commands.categories.appearance"),
				group: "action",
				icon: themeType === "dark" ? "light_mode" : "dark_mode",
				id: "theme.toggle",
				keywords: ["dark", "light", "mode", "appearance", "color"],
				label: t("commands.themeToggle", {
					mode: themeType === "dark" ? t("commands.themeMode.light") : t("commands.themeMode.dark")
				}),
				run: () => setThemeType(themeType === "dark" ? "light" : "dark")
			},
			{
				category: t("commands.categories.panels"),
				group: "action",
				icon: "view_sidebar",
				id: "panel.toggleDetail",
				keywords: ["detail", "panel", "sidebar", "side"],
				label: t("commands.toggleDetailPanel"),
				run: () => dispatch(togglePanel()),
				shortcutActionId: "toggleDetailPanel"
			},
			...(isAiChatEnabled
				? [
						{
							category: t("commands.categories.panels"),
							group: "action" as const,
							icon: "auto_awesome",
							id: "panel.toggleAi",
							keywords: ["ai", "fernie", "assistant", "chat"],
							label: t("commands.toggleAiPanel"),
							run: () => dispatch(toggleAiPanel()),
							shortcutActionId: "toggleAiPanel" as const
						}
					]
				: []),
			{
				category: t("commands.categories.help"),
				group: "action",
				icon: "keyboard",
				id: "help.shortcuts",
				keywords: ["shortcuts", "keys", "keyboard", "help"],
				label: t("commands.showShortcuts"),
				run: () => shortcutsOverlayControl.request(),
				shortcutActionId: "help.showShortcuts"
			},
			{
				category: t("commands.categories.navigation"),
				group: "action",
				icon: "menu",
				id: "focus.rail",
				keywords: ["rail", "menu", "sidebar"],
				label: t("commands.focusRail"),
				run: () => focusRail(),
				shortcutActionId: "focusRail"
			},
			{
				category: t("commands.categories.navigation"),
				group: "action",
				icon: "view_stream",
				id: "focus.ribbon",
				keywords: ["ribbon", "toolbar", "band"],
				label: t("commands.focusRibbon"),
				run: () => focusActiveRibbonBand(),
				shortcutActionId: "focusRibbon"
			},
			{
				category: t("commands.categories.navigation"),
				group: "action",
				icon: "search",
				id: "focus.search",
				keywords: ["search", "find"],
				label: t("commands.focusSearch"),
				run: () => globalSearchFocus.request(),
				shortcutActionId: "focusGlobalSearch"
			},
			{
				category: t("commands.categories.settings"),
				group: "action",
				icon: "display_settings",
				id: "goto.displaySettings",
				keywords: ["display", "settings", "mobile", "desktop"],
				label: t("commands.gotoDisplaySettings"),
				run: () => dispatch(openPreferencesModal("preferences"))
			},
			{
				category: t("commands.categories.settings"),
				group: "action",
				icon: "tune",
				id: "goto.preferences",
				keywords: ["preferences", "theme", "appearance"],
				label: t("commands.gotoPreferences"),
				run: () => dispatch(openPreferencesModal())
			},
			{
				category: t("commands.categories.settings"),
				group: "action",
				icon: "keyboard",
				id: "goto.shortcuts",
				keywords: ["shortcuts", "keys", "keyboard", "keybindings", "customize"],
				label: t("commands.gotoShortcuts"),
				run: () => dispatch(openPreferencesModal("shortcuts"))
			},
			{
				category: t("commands.categories.settings"),
				group: "action",
				icon: "person",
				id: "goto.profile",
				keywords: ["profile", "account", "me"],
				label: t("commands.gotoProfile"),
				run: () => dispatch(toggleProfilePanel())
			},
			{
				category: t("commands.categories.settings"),
				group: "action",
				icon: "settings",
				id: "goto.settings",
				keywords: ["settings", "configuration"],
				label: t("commands.gotoSettings"),
				run: () => runGuardedExit(() => navigate(SETTINGS_PATH))
			},
			{
				category: t("commands.categories.settings"),
				group: "action",
				icon: "hub",
				id: "goto.integrations",
				keywords: ["integrations", "connections", "api"],
				label: t("commands.gotoIntegrations"),
				run: () => runGuardedExit(() => navigate(INTEGRATIONS_PATH))
			},
			...(siteRequired
				? [
						{
							category: t("commands.categories.account"),
							group: "action" as const,
							icon: "location_on",
							id: "site.switch",
							keywords: ["site", "location", "switch", "select"],
							label: hasSite ? t("commands.switchSite") : t("commands.selectSite"),
							run: () => runGuardedExit(() => dispatch(updateSite(null)))
						}
					]
				: []),
			{
				category: t("commands.categories.account"),
				group: "action",
				icon: "logout",
				id: "auth.signOut",
				keywords: ["sign out", "logout", "log out", "exit"],
				label: t("commands.signOut"),
				run: () => runGuardedExit(() => sfAuth.redirectToLogout())
			}
		];

		const excludePaths = new Set([
			DISPLAY_SETTINGS_PATH,
			PREFERENCES_PATH,
			PROFILE_PATH,
			SETTINGS_PATH,
			INTEGRATIONS_PATH
		]);
		const navigateGuarded = (path: string) => runGuardedExit(() => navigate(path));
		const navigationCommands = buildNavigationCommands(
			getPageSearchIndex().filter((entry) => canOpenTenantSettings(entry.path, canManageSettings)),
			navigateGuarded,
			t,
			excludePaths
		);

		return [...actionCommands, ...navigationCommands];
	}, [navigate, setThemeType, themeType, isAiChatEnabled, siteRequired, hasSite, t, canManageSettings]);
};

export default useCommandCatalog;
